import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../ui/button";
import { RotateCw, Trophy, Pause, Play } from "lucide-react";

const COLS = 20;
const ROWS = 20;
const CELL = 18;
const SPEED_MS = 110;

const DIRS = {
    ArrowUp:    [0, -1],
    ArrowDown:  [0, 1],
    ArrowLeft:  [-1, 0],
    ArrowRight: [1, 0]
};

function newFood(snake) {
    while (true) {
        const f = [Math.floor(Math.random() * COLS), Math.floor(Math.random() * ROWS)];
        if (!snake.some(([x, y]) => x === f[0] && y === f[1])) return f;
    }
}

export function GameSnake({ onScoreSubmit }) {
    const [snake, setSnake] = useState([[10, 10], [9, 10], [8, 10]]);
    const [dir, setDir] = useState([1, 0]);
    const [food, setFood] = useState([15, 10]);
    const [score, setScore] = useState(0);
    const [best, setBest] = useState(() => parseInt(localStorage.getItem("crm_snake_best") || "0", 10));
    const [over, setOver] = useState(false);
    const [paused, setPaused] = useState(false);
    const dirRef = useRef(dir);
    const startedAt = useRef(Date.now());
    const submitted = useRef(false);

    useEffect(() => { dirRef.current = dir; }, [dir]);

    useEffect(() => {
        const onKey = (e) => {
            const d = DIRS[e.key];
            if (!d) return;
            e.preventDefault();
            // Prevent 180° turns
            if (d[0] === -dirRef.current[0] && d[1] === -dirRef.current[1]) return;
            setDir(d);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const tick = useCallback(() => {
        if (over || paused) return;
        setSnake((s) => {
            const head = s[0];
            const next = [(head[0] + dirRef.current[0] + COLS) % COLS, (head[1] + dirRef.current[1] + ROWS) % ROWS];

            // Self collision
            if (s.some(([x, y]) => x === next[0] && y === next[1])) {
                setOver(true);
                return s;
            }

            const ate = next[0] === food[0] && next[1] === food[1];
            const newSnake = [next, ...s];
            if (!ate) newSnake.pop();
            else {
                setScore((sc) => sc + 10);
                setFood(newFood(newSnake));
            }
            return newSnake;
        });
    }, [over, paused, food]);

    useEffect(() => {
        const id = setInterval(tick, SPEED_MS);
        return () => clearInterval(id);
    }, [tick]);

    useEffect(() => {
        if (over && !submitted.current) {
            submitted.current = true;
            if (score > best) {
                setBest(score);
                localStorage.setItem("crm_snake_best", String(score));
            }
            onScoreSubmit?.({ score, durationMs: Date.now() - startedAt.current });
        }
    }, [over, score, best, onScoreSubmit]);

    const reset = () => {
        setSnake([[10, 10], [9, 10], [8, 10]]);
        setDir([1, 0]);
        setFood([15, 10]);
        setScore(0);
        setOver(false);
        setPaused(false);
        submitted.current = false;
        startedAt.current = Date.now();
    };

    return (
        <div className="flex flex-col items-center w-full">
            <div className="flex items-center justify-between w-full mb-3" style={{ maxWidth: COLS * CELL }}>
                <div className="flex gap-2">
                    <ScoreBox label="Score" value={score} />
                    <ScoreBox label="Best" value={best} accent />
                </div>
                <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => setPaused((p) => !p)} disabled={over}>
                        {paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
                    </Button>
                    <Button size="sm" variant="outline" onClick={reset}><RotateCw className="h-3.5 w-3.5 mr-1" />New</Button>
                </div>
            </div>

            <div
                className="bg-gray-900 rounded-lg overflow-hidden relative shadow-lg"
                style={{ width: COLS * CELL, height: ROWS * CELL }}
            >
                {snake.map(([x, y], i) => (
                    <div
                        key={i}
                        style={{
                            position: "absolute",
                            left: x * CELL + 1, top: y * CELL + 1,
                            width: CELL - 2, height: CELL - 2,
                            background: i === 0 ? "#10B981" : "#34D399",
                            borderRadius: 3
                        }}
                    />
                ))}
                <div
                    style={{
                        position: "absolute",
                        left: food[0] * CELL + 2, top: food[1] * CELL + 2,
                        width: CELL - 4, height: CELL - 4,
                        background: "#EF4444",
                        borderRadius: "50%"
                    }}
                />
                {over && (
                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                        <div className="text-center text-white">
                            <Trophy className="h-7 w-7 text-amber-400 mx-auto mb-1" />
                            <div className="text-lg font-bold">Game Over</div>
                            <div className="text-sm opacity-80">Score: {score}</div>
                        </div>
                    </div>
                )}
                {paused && !over && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <div className="text-white text-2xl font-bold">PAUSED</div>
                    </div>
                )}
            </div>

            <p className="text-[11px] text-gray-400 mt-3">Arrow keys to move · walls wrap around</p>
        </div>
    );
}

function ScoreBox({ label, value, accent }) {
    return (
        <div className={`px-3 py-1.5 rounded-md text-center ${accent ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-700"}`}>
            <div className="text-[10px] uppercase font-semibold tracking-wide opacity-70">{label}</div>
            <div className="text-base font-bold tabular-nums">{value}</div>
        </div>
    );
}
